import { Badge, Select, Switch } from '@artosera/ui';
import {
  DROITS_FINS,
  ROLES_GALERIE,
  droitAccordable,
  formatDate,
  type DroitsFins,
  type RoleGalerie,
} from '@artosera/shared';
import type { JSX, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { MembreEquipe } from '../../api/equipe.js';
import { Monogramme } from '../../composants/Monogramme.js';
import styles from './EquipePage.module.css';

export interface LigneMembreProps {
  readonly membre: MembreEquipe;
  readonly connexionSso?: ReactNode;
  /** La direction peut changer le rôle de ce membre (ni soi, ni support). */
  readonly modifiable: boolean;
  /** Les droits fins s'appliquent (module actif) : interrupteurs actifs. */
  readonly droitsModifiables: boolean;
  readonly soi: boolean;
  readonly enCours: boolean;
  readonly onRole: (role: RoleGalerie) => void;
  readonly onDroits: (droits: DroitsFins) => void;
}

/** Une personne de l'équipe : identité, rôle simple, droits fins (M7). */
export function LigneMembre({
  membre,
  connexionSso,
  modifiable,
  droitsModifiables,
  soi,
  enCours,
  onRole,
  onDroits,
}: LigneMembreProps): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const accordes = DROITS_FINS.filter((droit) => membre.droits[droit]);

  return (
    <div
      className={styles.ligne}
      data-ui="team-member"
      data-membre={membre.compteId}
      data-sso={connexionSso !== undefined}
      role="row"
    >
      <div className={styles.identite} role="cell">
        <Monogramme nom={membre.nom} />
        <span className={styles.nom}>{membre.nom}</span>
        <span className={styles.email}>{membre.email}</span>
        <span className={styles.connexion}>
          {t('regie:reglages.equipe.colonneDerniereConnexion')}
          {' : '}
          {membre.derniereConnexion
            ? formatDate(new Date(membre.derniereConnexion), langue, 'court')
            : t('regie:reglages.equipe.jamaisConnecte')}
        </span>
        {soi ? <Badge tone="neutral">{t('regie:reglages.equipe.vous')}</Badge> : null}
        {membre.support && membre.expireLe ? (
          <Badge tone="neutral">
            {t('regie:reglages.equipe.support', {
              date: formatDate(new Date(membre.expireLe), langue, 'court'),
            })}
          </Badge>
        ) : null}
      </div>
      <div className={styles.role} role="cell">
        {modifiable ? (
          <Select
            aria-label={t('regie:reglages.equipe.roleDe', { nom: membre.nom })}
            value={membre.role}
            disabled={enCours}
            options={ROLES_GALERIE.map((role) => ({
              value: role,
              label: t(`commun:roles.${role}`),
            }))}
            onValueChange={(valeur) => {
              if (valeur !== membre.role) onRole(valeur as RoleGalerie);
            }}
          />
        ) : (
          <Badge tone={membre.role === 'direction' ? 'accent' : 'neutral'}>
            {t(`commun:roles.${membre.role}`)}
          </Badge>
        )}
      </div>
      <div className={styles.droits} role="cell">
        {modifiable ? (
          DROITS_FINS.map((droit) =>
            droitAccordable(membre.role, droit) ? (
              <Switch
                key={droit}
                label={t(`commun:droits.${droit}`)}
                checked={membre.droits[droit]}
                disabled={!droitsModifiables || enCours}
                onCheckedChange={(coche) => onDroits({ ...membre.droits, [droit]: coche })}
              />
            ) : null,
          )
        ) : accordes.length > 0 ? (
          <span>{accordes.map((droit) => t(`commun:droits.${droit}`)).join(', ')}</span>
        ) : (
          <span className={styles.discret}>{t('regie:reglages.equipe.aucunDroit')}</span>
        )}
        {modifiable && DROITS_FINS.every((droit) => !droitAccordable(membre.role, droit)) ? (
          <span className={styles.discret}>
            {membre.role === 'direction'
              ? t('regie:reglages.equipe.parSonRole')
              : t('regie:reglages.equipe.nonAccordable')}
          </span>
        ) : null}
      </div>
      {connexionSso !== undefined ? (
        <div className={styles.sso} role="cell">
          {connexionSso}
        </div>
      ) : null}
    </div>
  );
}
